import React, { useState } from "react";
import { motion } from "motion/react";
import { Plus, Check } from "lucide-react";
import { useStore } from "@/src/contexts/StoreContext";
import { cn } from "@/src/lib/utils";

interface ProductCardProps {
  key?: string | number;
  product: {
    id: number;
    name: string;
    description: string;
    category: string;
    base_price: number;
    variants: Array<{
      id: number;
      variant_name: string;
      size_description: string;
      price_modifier: number;
    }>;
  };
  imageUrl: string;
  onAdded?: () => void;
}

export function ProductCard({ product, imageUrl, onAdded }: ProductCardProps) {
  const { addToCart, getCategoryIcon, formatPrice } = useStore();
  const [selectedVariant, setSelectedVariant] = useState(product.variants[0]);
  const [isAdded, setIsAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart(product, selectedVariant, imageUrl);
    setIsAdded(true);
    onAdded?.();
    setTimeout(() => setIsAdded(false), 2000);
  };

  const totalPrice = product.base_price + selectedVariant.price_modifier;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 transition-all"
    >
      {/* Product Icon/Preview */}
      <div className="aspect-[4/3] bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center relative overflow-hidden">
        <div className="text-6xl">{getCategoryIcon(product.category)}</div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className="px-2 py-1 bg-black/50 backdrop-blur-md rounded text-xs font-medium uppercase tracking-wider text-white/80">
            {product.category}
          </span>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-white">{product.name}</h3>
          <p className="text-sm text-white/50 mt-1">{product.description}</p>
        </div>

        {/* Size Selector */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-white/60 uppercase tracking-wider">Size</label>
          <div className="flex flex-wrap gap-2">
            {product.variants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => setSelectedVariant(variant)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                  selectedVariant.id === variant.id
                    ? "bg-white text-black"
                    : "bg-white/5 text-white/60 hover:bg-white/10"
                )}
              >
                {variant.variant_name}
              </button>
            ))}
          </div>
          <p className="text-xs text-white/40">{selectedVariant.size_description}</p>
        </div>

        {/* Price & Add to Cart */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <span className="text-2xl font-bold text-white">{formatPrice(totalPrice)}</span>
            {selectedVariant.price_modifier > 0 && (
              <span className="text-xs text-white/40 ml-2">
                +{formatPrice(selectedVariant.price_modifier)}
              </span>
            )}
          </div>
          <button
            onClick={handleAddToCart}
            disabled={isAdded}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all",
              isAdded
                ? "bg-green-500 text-white"
                : "bg-white text-black hover:bg-white/90 hover:scale-105"
            )}
          >
            {isAdded ? (
              <>
                <Check className="w-4 h-4" />
                Added
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}